<template>
    <div id="email-login-form">
        <div v-if="error" class="error-message" :class="errorType" :innerText="error"></div>

        <Loader v-if="isProcessing" indefinite />

        <form v-else-if="ssoButtonCallback">
            <button @click="ssoButtonCallback" class="zs-btn zs-btn--primary">Continue with Single Sign On</button>
            <button class="zs-btn" @click="ssoButtonCallback = undefined">Cancel</button>
        </form>

        <form v-else-if="totpCeremonyId" @submit.prevent="completeTotpLogin">
            <p class="zs-muted text-center mb-2">Enter the code from your authenticator app or a recovery code.</p>
            <input v-model="totpCode" v-autofocus autocomplete="one-time-code" inputmode="numeric" placeholder="authentication code" />
            <button class="zs-btn zs-btn--primary">Continue</button>
            <button type="button" class="zs-btn" @click="cancelTotp">Cancel</button>
        </form>

        <div v-else-if="emailLinkSent" class="email-link-sent">
            <p>Check your email for a secure sign-in link.</p>
            <button class="zs-btn" @click="emailLinkSent = false">Use another method</button>
        </div>

        <form v-else ref="form" @submit.prevent="login">
            <input v-model="email" v-autofocus="!enablePassword" type="email" placeholder="e-mail address" />
            <input v-if="enablePassword" v-model="password" v-autofocus type="password" placeholder="password" />
            <button class="zs-btn zs-btn--primary">Continue</button>

            <template v-if="!enablePassword">
                <button type="button" class="zs-btn sso sso-microsoft mt-2" @click="startSsoLogin({ provider: 'azure' })">
                    <img src="/assets/login-ms.svg" /> Sign in with Microsoft
                </button>
                <button type="button" class="zs-btn sso sso-google" @click="startSsoLogin({ provider: 'google' })">
                    <img src="/assets/login-google.svg" /> Sign in with Google
                </button>
                <button type="button" class="zs-btn sso" @click="startPasskeyLogin()"><i class="fa-solid fa-key"></i> Sign in with a passkey</button>
            </template>

            <label class="remember-me" :class="{ active: rememberMe }">
                <input type="checkbox" v-model="rememberMe" />
                <i :class="rememberMe ? 'fa-solid fa-circle-check' : 'fa-light fa-circle'"></i>
                Keep me signed in for 30 days
            </label>
        </form>
    </div>
</template>

<script lang="ts" setup>
import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { formatError, handleError, maskForm, unmaskForm } from '@zyno-io/vue-foundation';
import { ref } from 'vue';

import { GlobalState } from '@/global';
import { AuthLoginApi, type ILoginResponse, type ITotpChallengeResponse } from '@/openapi-clients-generated/auth';
import Loader from '@/shared/components/loader.vue';
import { useBroadcastMessageMonitor } from '@/shared/helpers/broadcast.helpers';
import { isCancelledWebAuthnCeremony } from '@/shared/helpers/webauthn.helpers';
import { Auth } from '@/shared/services/auth.service';

import { completePasskeyLogin, loginWithPasskey } from './passkey-login';

const form = ref<HTMLFormElement>();
const error = ref<null | string>(null);
const errorType = ref<string | null>();
const email = ref(emailFromSignupHandoff());
const password = ref('');

const isProcessing = ref(false);
const ssoButtonCallback = ref<() => void>();
const enablePassword = ref(false);
const rememberMe = ref(false);
const emailLinkSent = ref(false);
const totpCeremonyId = ref(sessionStorage.getItem('zs:auth:totpCeremonyId'));
const totpCode = ref('');
if (totpCeremonyId.value) sessionStorage.removeItem('zs:auth:totpCeremonyId');

const msgMonitor = useBroadcastMessageMonitor('login');

function emailFromSignupHandoff(): string {
    const encoded = /(?:^#|;)email:([^;]+)/.exec(location.hash)?.[1];
    if (!encoded) return '';
    try {
        return decodeURIComponent(encoded);
    } catch {
        return '';
    }
}

function clearError() {
    errorType.value = null;
    error.value = null;
}

async function login() {
    if (!email.value.trim().length) {
        return;
    }
    if (enablePassword.value && !password.value.length) {
        return;
    }

    maskForm(form.value!);
    clearError();

    try {
        if (enablePassword.value) {
            const { data } = await AuthLoginApi.postLoginPasswordLogin<true>({
                body: {
                    browserId: GlobalState.browserId,
                    email: email.value,
                    password: password.value,
                    rememberMe: rememberMe.value
                },
                credentials: 'include'
            });
            if (isTotpChallenge(data)) {
                startTotpChallenge(data.ceremonyId);
            } else {
                await Auth.processLoginResponse(data);
            }
        } else {
            const response = dataFrom(
                await AuthLoginApi.postLoginLogin({
                    body: { email: email.value, redirectUrl: `${location.origin}/login/callback` }
                })
            );
            if (response.next === 'oauth') {
                startSsoLogin({ authorizeUrl: response.authorizeUrl });
            } else if (response.next === 'passkey') {
                await Auth.processLoginResponse(await completePasskeyLogin(response, rememberMe.value));
            } else if (response.next === 'password') {
                unmaskForm(form.value!);
                enablePassword.value = true;
            } else {
                emailLinkSent.value = true;
                unmaskForm(form.value!);
            }
        }
    } catch (err) {
        unmaskForm(form.value!);
        handleLoginError(err);
    }
}

async function startPasskeyLogin() {
    maskForm(form.value!);
    clearError();
    try {
        await Auth.processLoginResponse(await loginWithPasskey(undefined, rememberMe.value));
    } catch (err) {
        unmaskForm(form.value!);
        handleLoginError(err);
    }
}

function startTotpChallenge(ceremonyId: string) {
    totpCeremonyId.value = ceremonyId;
    totpCode.value = '';
    unmaskForm(form.value!);
}

async function completeTotpLogin() {
    if (!totpCeremonyId.value || !totpCode.value.trim()) return;
    isProcessing.value = true;
    clearError();
    try {
        const { data } = await AuthLoginApi.postLoginTotpLogin<true>({
            body: { ceremonyId: totpCeremonyId.value, code: totpCode.value.trim() },
            credentials: 'include'
        });
        await Auth.processLoginResponse(data);
    } catch (err) {
        handleLoginError(err);
    }
}

function cancelTotp() {
    totpCeremonyId.value = null;
    totpCode.value = '';
    isProcessing.value = false;
}

function isTotpChallenge(value: ILoginResponse | ITotpChallengeResponse): value is ITotpChallengeResponse {
    return 'next' in value && value.next === 'totp';
}

async function startSsoLogin({ provider, authorizeUrl }: { provider?: 'azure' | 'google'; authorizeUrl?: string }) {
    clearError();

    const loginWindow = window.open(
        authorizeUrl ?? `/login/sso?provider=${provider}`,
        'loginSSO',
        'width=450,height=550,popup=true,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!loginWindow) {
        ssoButtonCallback.value = () => {
            ssoButtonCallback.value = undefined;
            startSsoLogin({ provider, authorizeUrl });
        };
        return;
    }

    isProcessing.value = true;

    let windowClosed = false;
    const windowClosePromise = new Promise<void>(resolve => {
        const interval = setInterval(() => {
            try {
                if (loginWindow.closed) {
                    windowClosed = true;
                    clearInterval(interval);
                    resolve();
                }
            } catch {
                /**/
            }
        }, 500);
    });
    const messagePromise = msgMonitor.waitForMessage<Record<string, string>>();
    await Promise.race([windowClosePromise, messagePromise]);

    // if the window was closed, we don't need to do anything
    if (windowClosed) {
        isProcessing.value = false;
        return;
    }

    const urlParams = await messagePromise;
    if (!urlParams) {
        return handleLoginError('Missing callback parameters', true);
    }

    if (urlParams.error) {
        return handleLoginError(`SSO error: ${urlParams.error}: ${urlParams.error_description}`, true);
    }

    if (!urlParams.code) {
        return handleLoginError('Missing code parameter', true);
    }

    const code = urlParams.code!;
    const state = urlParams.state!;

    // clear any existing session so that it's not included in the login request
    Auth.clearStoredJwt();

    try {
        const { data } = await AuthLoginApi.postLoginSsoCallback<true>({
            body: {
                browserId: GlobalState.browserId,
                redirectUrl: `${location.origin}/login/callback`,
                code,
                state,
                rememberMe: rememberMe.value
            },
            credentials: 'include'
        });
        await Auth.processLoginResponse(data);
    } catch (err) {
        return handleLoginError(err);
    }
}

// eslint-disable-next-line no-explicit-any
function handleLoginError(err: any, isCritical?: boolean) {
    // Backing out of the passkey prompt is a choice, not a failure. The form is already
    // restored by the caller, so it simply returns — with nothing to report, and nothing
    // to log, since the browser reports a cancel and a timeout identically by design.
    if (isCancelledWebAuthnCeremony(err)) {
        errorType.value = null;
        isProcessing.value = false;
        return;
    }

    if (err instanceof Error || isCritical) {
        handleError(err);
        error.value = String(err).includes('invalid_credentials')
            ? 'The e-mail address or password you provided is incorrect. Please try again. If you need further assistance, please contact support@zyno.app'
            : formatError(err);
    } else {
        error.value = err;
    }

    errorType.value = null;
    isProcessing.value = false;
}

const sessionLogoutCause = Auth.logoutCauseOnce;
if (sessionLogoutCause) {
    error.value = sessionLogoutCause;
    errorType.value = 'warning';
}
</script>

<style scoped>
@reference '#tw.css';

#email-login-form {
    @apply max-w-xs w-screen;
}

form {
    @apply flex flex-col gap-1;
}

.sso {
    @apply bg-surface border-line-2 hover:bg-sunk flex gap-2;

    img {
        @apply max-w-6;
    }
}

.email-link-sent {
    @apply flex flex-col gap-3 text-center text-ink-2;
}

.remember-me {
    @apply mt-2 flex items-center gap-2 text-ink-3 cursor-pointer hover:bg-surface-3 border border-line rounded-md p-1 justify-center text-sm;

    &.active {
        @apply text-accent-ink bg-accent-soft;
    }

    input {
        @apply hidden;
    }

    i {
        @apply text-base;
    }
}
</style>
